import { type EventParticipant, type GuestRow, type ParticipantData, type ParticipantDataField } from './guest-list-types';

// Port of the legacy EditEventViewModel.processGuestList / setDefaultValues.
// Primary guests (with ContactId) carry their accompanying guests' form data in EventParticipantData.Participants,
// keyed by Sqiid. Accompanying guests (no ContactId) resolve their display data from that entry, falling back to
// their own EventParticipantData, and are rendered directly under their parent. Accompanying guests whose parent
// is not among the loaded participants are rendered last.

const parseData = (raw: string | null): ParticipantData | undefined => {
    if (!raw) return undefined;
    try {
        return JSON.parse(raw) ?? {};
    } catch {
        return undefined;
    }
};

const sqiidKey = (sqiid: unknown, parentId: string) => `SQIID-${sqiid}_${parentId}`;

const applyDefaultValue = (field: ParticipantDataField, data: ParticipantData) => {
    let match = field.DefaultValue?.match(/(Email|SmsNumber|Name|FirstName|LastName|CompanyName)/);
    if (match) {
        data[match[0]] = field.Value;
        return;
    }

    match = field.Title?.match(/(Namn)/);
    if (match) data.Name = field.Value;

    match = field.Title?.match(/(E-post)/);
    if (match) data.Email = field.Value;
};

export function buildGuestRows(participants: EventParticipant[]): GuestRow[] {
    const loadedIds = new Set(participants.map((p) => p.EventParticipantId));
    const accompanyingBySqiid = new Map<string, ParticipantData>();
    const dataById = new Map<string, ParticipantData>();

    for (const participant of participants) {
        if (!participant.ContactId) continue;

        const data = parseData(participant.EventParticipantData) ?? {};
        dataById.set(participant.EventParticipantId, data);

        data.Participants?.forEach((accompanying) => {
            const key = sqiidKey(accompanying.Sqiid, participant.EventParticipantId);
            if (!accompanyingBySqiid.has(key)) accompanyingBySqiid.set(key, accompanying);
        });
    }

    const childrenByParent = new Map<string, GuestRow[]>();
    const orphans: GuestRow[] = [];

    for (const participant of participants) {
        if (participant.ContactId || !participant.EventParticipantParentId) continue;

        const parsed = parseData(participant.EventParticipantData);
        const source = accompanyingBySqiid.get(sqiidKey(parsed?.Sqiid, participant.EventParticipantParentId)) ?? parsed ?? {};
        const data: ParticipantData = { ...source };
        source.Fields?.forEach((field) => applyDefaultValue(field, data));

        if (!loadedIds.has(participant.EventParticipantParentId)) {
            orphans.push({ id: participant.EventParticipantId, participant, data, kind: 'orphanChild' });
            continue;
        }

        const siblings = childrenByParent.get(participant.EventParticipantParentId) ?? [];
        siblings.push({ id: participant.EventParticipantId, participant, data, kind: 'child' });
        childrenByParent.set(participant.EventParticipantParentId, siblings);
    }

    const rows: GuestRow[] = [];

    for (const participant of participants) {
        if (participant.EventParticipantParentId) continue;

        rows.push({ id: participant.EventParticipantId, participant, data: dataById.get(participant.EventParticipantId) ?? {}, kind: 'primary' });
        rows.push(...(childrenByParent.get(participant.EventParticipantId) ?? []));
    }

    return [...rows, ...orphans];
}

/** Merges participants into the list, distinct on EventParticipantId — later entries replace earlier ones in place. */
export function mergeParticipants(existing: EventParticipant[], incoming: EventParticipant[]): EventParticipant[] {
    const result = [...existing];
    const indexById = new Map(result.map((p, index) => [p.EventParticipantId, index]));

    for (const participant of incoming) {
        const index = indexById.get(participant.EventParticipantId);
        if (index === undefined) {
            indexById.set(participant.EventParticipantId, result.length);
            result.push(participant);
        } else {
            result[index] = participant;
        }
    }

    return result;
}
